import React, { useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { api } from 'common';
import { getLangKey } from 'common/src/other/getLangKey';
import CircularLoading from '../components/CircularLoading';
import localization from '../components/Table/Localization';
import C2Dialog, { C2DangerButton, C2PrimaryButton, C2SecondaryButton } from '../components/C2Dialog';
import {
  C2,
  C2_FONT,
  C2AddButton,
  C2DocsChip,
  C2EmptyState,
  C2SectionHeading,
  C2TableCard,
  c2Localization,
  c2TableOptions,
  c2TableStyle,
  c2TableTheme,
  c2Toolbar,
} from '../components/Table/C2UserTable';
import { DemoModeAlert, SettingsField, SettingsNotice } from '../components/settings/C2SettingsKit';

const cleanList = (list) => list.map(({ label, value }) => ({ label, value }));

export default function CancellationReasons() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { editCancellationReason, convertLanguage } = api;
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const cancelreasondata = useSelector((state) => state.cancelreasondata);
  const auth = useSelector((state) => state.auth);
  const canEdit = !!settings?.AllowCriticalEditsAdmin;
  const reasons = useMemo(
    () => Object.values(cancelreasondata.complex || {}).filter((r) => r && r.label).map((r) => ({ label: r.label, value: r.value })),
    [cancelreasondata.complex]
  );
  const [editor, setEditor] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const userLocale = auth?.profile?.lang?.langLocale || null;

  const persist = async (list, method, successMsg) => {
    setBusy(true);
    try {
      await dispatch(editCancellationReason(cleanList(list), method));
      setNotice({ severity: 'success', message: successMsg });
      return true;
    } catch (e) {
      setNotice({ severity: 'error', message: `No se pudo guardar: ${e?.message || e}` });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const guardEdit = () => {
    if (!canEdit) setNotice({ severity: 'info', message: 'Modo demostración: los cambios no se guardan.' });
    return canEdit;
  };

  const openEditor = (row) => {
    if (!guardEdit()) return;
    setEditor(row ? { value: row.value, label: row.label, original: row.label } : { value: null, label: '' });
  };

  const saveEditor = async () => {
    const label = String(editor.label || '').trim();
    if (!label) return;
    const duplicated = reasons.some((r) => r.value !== editor.value && String(r.label).trim().toLowerCase() === label.toLowerCase());
    if (duplicated) {
      setEditor({ ...editor, error: 'Ya existe un motivo con ese texto.' });
      return;
    }
    let list;
    let method;
    if (editor.value === null) {
      const nextValue = reasons.reduce((max, r) => Math.max(max, Number(r.value) || 0), -1) + 1;
      list = [...reasons, { label, value: nextValue }];
      method = 'Add';
    } else {
      list = reasons.map((r) => (r.value === editor.value ? { ...r, label } : r));
      method = 'Update';
    }
    const ok = await persist(list, method, method === 'Add' ? 'Motivo agregado.' : 'Motivo actualizado.');
    if (ok) {
      if (label !== editor.original) Promise.resolve(convertLanguage(label, userLocale)).catch(() => null);
      setEditor(null);
    }
  };

  const confirmDelete = async () => {
    const list = reasons.filter((r) => r.value !== toDelete.value);
    const ok = await persist(list, 'Delete', 'Motivo eliminado.');
    if (ok) setToDelete(null);
  };

  const columns = [
    {
      title: 'Motivo',
      field: 'label',
      render: (row) => {
        const translated = t(getLangKey(row.label));
        const shown = translated && translated !== getLangKey(row.label) ? translated : row.label;
        return (
          <Box>
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 600, fontSize: '0.9rem', color: C2.dark }}>{shown}</Typography>
            {shown !== row.label ? (
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.76rem', color: C2.muted }}>Texto original: {row.label}</Typography>
            ) : null}
          </Box>
        );
      },
    },
  ];

  if (cancelreasondata.loading) return <CircularLoading />;

  return (
    <ThemeProvider theme={c2TableTheme}>
      {!canEdit ? <DemoModeAlert /> : null}
      <SettingsNotice notice={notice} onClose={() => setNotice(null)} />
      <C2TableCard>
        <C2SectionHeading
          title="Motivos de cancelación"
          subtitle="Opciones que ven conductores y usuarios al cancelar un viaje. Se traducen automáticamente a los demás idiomas."
        />
        <MaterialTable
          title={t('cancellation_reasons_title')}
          columns={columns}
          data={reasons}
          style={c2TableStyle(isRTL)}
          components={c2Toolbar(<C2AddButton key="add" label="Nuevo motivo" onClick={() => openEditor(null)} />)}
          onRowClick={(evt, row) => setSelectedRow(row.tableData.id)}
          options={{ ...c2TableOptions(selectedRow), actionsColumnIndex: -1, pageSize: 10, exportButton: false }}
          localization={c2Localization(
            localization(t),
            <C2EmptyState
              icon={EventBusyOutlinedIcon}
              title="Aún no hay motivos de cancelación"
              message="Agrega los motivos más comunes para que cancelar un viaje sea rápido y puedas entender por qué ocurre."
            />
          )}
          actions={[
            {
              icon: () => <C2DocsChip icon={EditOutlinedIcon} label="Editar" />,
              tooltip: 'Editar motivo',
              onClick: (e, row) => openEditor(row),
            },
            {
              icon: () => <DeleteOutlineRoundedIcon sx={{ color: '#B91C1C' }} />,
              tooltip: 'Eliminar motivo',
              onClick: (e, row) => guardEdit() && setToDelete(row),
            },
          ]}
        />
      </C2TableCard>

      <C2Dialog
        open={!!editor}
        onClose={() => !busy && setEditor(null)}
        title={editor && editor.value !== null ? 'Editar motivo' : 'Nuevo motivo'}
        icon={EventBusyOutlinedIcon}
        actions={
          <>
            <C2SecondaryButton onClick={() => setEditor(null)} disabled={busy}>Cancelar</C2SecondaryButton>
            <C2PrimaryButton onClick={saveEditor} disabled={busy || !String(editor?.label || '').trim()}>
              {busy ? 'Guardando…' : 'Guardar'}
            </C2PrimaryButton>
          </>
        }
      >
        {editor ? (
          <SettingsField
            name="label"
            label="Motivo"
            value={editor.label}
            onChange={(name, v) => setEditor({ ...editor, label: v, error: null })}
            error={editor.error}
            helper="Escríbelo en tu idioma; se traducirá a los demás al guardar."
            autoFocus
            inputProps={{ maxLength: 120 }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEditor(); } }}
          />
        ) : null}
      </C2Dialog>

      <C2Dialog
        open={!!toDelete}
        onClose={() => !busy && setToDelete(null)}
        title="Eliminar motivo"
        icon={DeleteOutlineRoundedIcon}
        actions={
          <>
            <C2SecondaryButton onClick={() => setToDelete(null)} disabled={busy}>Cancelar</C2SecondaryButton>
            <C2DangerButton onClick={confirmDelete} disabled={busy}>{busy ? 'Eliminando…' : 'Eliminar'}</C2DangerButton>
          </>
        }
      >
        <Typography sx={{ fontFamily: C2_FONT, color: C2.muted }}>
          ¿Eliminar “{toDelete?.label}”? Dejará de aparecer como opción al cancelar un viaje. Las cancelaciones anteriores conservan su motivo.
        </Typography>
      </C2Dialog>
    </ThemeProvider>
  );
}
